import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  width: 100%;
  max-width: 1000px;
  margin-bottom: 30px;
`;

const NavLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 12px 20px;
  color: white;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
`;

const ProfileCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 40px;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 30px;
  text-align: center;
`;

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const UserName = styled.h1`
  color: white;
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 10px;
`;

const UserTitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  margin-bottom: 30px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1000px;
  margin-bottom: 40px;
`;

const ActionCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 25px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
`;

const ActionIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 15px;
`;

const ActionTitle = styled.div`
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ActionDesc = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const Section = styled.div`
  width: 100%;
  max-width: 1000px;
`;

const SectionTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HistoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`;

const HistoryCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  }
`;

const HistoryImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const HistoryContent = styled.div`
  padding: 15px;
`;

const HistoryPrompt = styled.div`
  color: white;
  font-size: 0.9rem;
  margin-bottom: 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const HistoryMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.6);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
`;

const EmptyText = styled.div`
  font-size: 1.2rem;
  margin-bottom: 10px;
`;

const EmptySubtext = styled.div`
  font-size: 1rem;
`;

function UserProfile() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalWorks: 0,
    todayCount: 0,
    privileges: '∞'
  });

  useEffect(() => {
    fetchUserHistory();
  }, []);

  // 格式化时间为北京时间
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    // 转换为北京时间 (UTC+8)
    const beijingTime = new Date(date.getTime() + (8 * 60 * 60 * 1000));
    return beijingTime.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Shanghai'
    });
  };

  const fetchUserHistory = async () => {
    try {
      const response = await axios.get('/api/tasks');
      if (response.data.success) {
        setHistory(response.data.data);
        // 更新统计数据
        setStats({
          totalWorks: response.data.data.length,
          todayCount: response.data.data.filter(item => {
            const today = new Date().toISOString().split('T')[0];
            return item.created_at.startsWith(today);
          }).length,
          privileges: '∞'
        });
      }
    } catch (error) {
      console.error('获取历史记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <NavLink to="/">
          ← 返回专属创作空间
        </NavLink>
      </Header>
      
      <ProfileCard>
        <Avatar>👤</Avatar>
        <UserName>{user?.username || '尊贵的知识星球会员'}</UserName>
        <UserTitle>👑 梭哈AI知识星球 · 尊贵会员专享Midjourney服务</UserTitle>
        
        <StatsGrid>
          <StatItem>
            <StatNumber>{stats.totalWorks}</StatNumber>
            <StatLabel>专属作品</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{stats.todayCount}</StatNumber>
            <StatLabel>今日创作</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>{stats.privileges}</StatNumber>
            <StatLabel>尊贵特权</StatLabel>
          </StatItem>
        </StatsGrid>
      </ProfileCard>
      
      <ActionsGrid>
        <ActionCard onClick={() => window.location.href = '/'}>
          <ActionIcon>✨</ActionIcon>
          <ActionTitle>专属Midjourney创作</ActionTitle>
          <ActionDesc>尊贵会员无限制体验</ActionDesc>
        </ActionCard>
        
        <ActionCard>
          <ActionIcon>👑</ActionIcon>
          <ActionTitle>会员专属特权</ActionTitle>
          <ActionDesc>查看您的尊贵权益</ActionDesc>
        </ActionCard>
        
        <ActionCard>
          <ActionIcon>🎯</ActionIcon>
          <ActionTitle>梭哈AI知识星球</ActionTitle>
          <ActionDesc>与其他尊贵会员交流</ActionDesc>
        </ActionCard>
      </ActionsGrid>
      
      <Section>
        <SectionTitle>
          <span>🎨</span>
          我的专属艺术作品集
        </SectionTitle>
        
        {loading ? (
          <EmptyState>
            <EmptyIcon>⏳</EmptyIcon>
            <EmptyText>加载中...</EmptyText>
          </EmptyState>
        ) : history.length > 0 ? (
          <HistoryGrid>
            {history.map((item) => (
              <HistoryCard key={item.id}>
                {item.result_images && item.result_images.length > 0 && (
                  <HistoryImage 
                    src={item.result_images[0]} 
                    alt="生成的图片"
                    onError={(e) => {
                      e.target.src = 'https://picsum.photos/280/200?random=' + item.id;
                    }}
                  />
                )}
                <HistoryContent>
                  <HistoryPrompt>{item.prompt}</HistoryPrompt>
                  <HistoryMeta>
                    <span>{item.aspect_ratio}</span>
                    <span>{formatDate(item.created_at)}</span>
                  </HistoryMeta>
                </HistoryContent>
              </HistoryCard>
            ))}
          </HistoryGrid>
        ) : (
          <EmptyState>
            <EmptyIcon>✨</EmptyIcon>
            <EmptyText>还没有创作记录</EmptyText>
            <EmptySubtext>开始您的专属艺术创作之旅吧！</EmptySubtext>
          </EmptyState>
        )}
      </Section>
    </Container>
  );
}

export default UserProfile;